import React from 'react';

export interface TotalSummaryGraphicProps {
  name: string;
  title: string;
  public: number;
  private: number;
  gdpr: number | undefined;
  decimalComma?: boolean;
}

const MIN_WIDTH = 58;
const MIN_ICON_WIDTH = 40;

export function TotalSummaryGraphic(props: TotalSummaryGraphicProps) {
  const [publicValue, setPublicValue] = React.useState('');
  const [privateValue, setPrivateValue] = React.useState('');
  const [gdprValue, setGdprValue] = React.useState('');

  const calculateElemetsValues = () => {
    setPublicValue('');
    setPrivateValue('');
    setGdprValue('');

    const container = document.getElementById(`${props.name}-graph-container`);
    if (container) {
      const containerWidth = container.clientWidth;
      const totalValue = props.public + props.private + (props.gdpr || 0);

      let publicWidth = containerWidth * (props.public / totalValue);
      let privateWidth = containerWidth * (props.private / totalValue);
      let gdprWidth = MIN_ICON_WIDTH;

      if (props.gdpr === undefined) {
        if (publicWidth < MIN_WIDTH || privateWidth < MIN_WIDTH) {
          if (publicWidth < MIN_WIDTH && privateWidth < MIN_WIDTH) {
            publicWidth = props.public === 0 ? 0 : MIN_WIDTH;
            privateWidth = props.private === 0 ? 0 : MIN_WIDTH;
          } else if (publicWidth < MIN_WIDTH) {
            publicWidth = props.public === 0 ? 0 : MIN_WIDTH;
            privateWidth = containerWidth - (publicWidth + gdprWidth);
          } else if (privateWidth < MIN_WIDTH) {
            privateWidth = props.private === 0 ? 0 : MIN_WIDTH;
            publicWidth = containerWidth - (privateWidth + gdprWidth);
          }
        }
        if (props.public === 0 && props.private === 0) {
          gdprWidth = containerWidth;
        }
      } else {
        gdprWidth = containerWidth * (props.gdpr / totalValue);
        if (
          (publicWidth <= MIN_WIDTH && props.public !== 0) ||
          (privateWidth <= MIN_WIDTH && props.private !== 0) ||
          (gdprWidth <= MIN_WIDTH && props.gdpr !== 0)
        ) {
          if (publicWidth < MIN_WIDTH && privateWidth < MIN_WIDTH && gdprWidth < MIN_WIDTH) {
            gdprWidth = props.gdpr === 0 ? 0 : MIN_WIDTH;
            publicWidth = props.public === 0 ? 0 : MIN_WIDTH;
            privateWidth = props.private === 0 ? 0 : MIN_WIDTH;
          } else if (publicWidth < MIN_WIDTH && privateWidth < MIN_WIDTH) {
            publicWidth = props.public === 0 ? 0 : MIN_WIDTH;
            privateWidth = props.private === 0 ? 0 : MIN_WIDTH;
            gdprWidth = containerWidth - (publicWidth + privateWidth);
          } else if (publicWidth < MIN_WIDTH && gdprWidth < MIN_WIDTH) {
            publicWidth = props.public === 0 ? 0 : MIN_WIDTH;
            gdprWidth = props.gdpr === 0 ? 0 : MIN_WIDTH;
            privateWidth = containerWidth - (publicWidth + gdprWidth);
          } else if (gdprWidth < MIN_WIDTH && privateWidth < MIN_WIDTH) {
            gdprWidth = props.gdpr === 0 ? 0 : MIN_WIDTH;
            privateWidth = props.private === 0 ? 0 : MIN_WIDTH;
            publicWidth = containerWidth - (privateWidth + gdprWidth);
          } else if (publicWidth < MIN_WIDTH && props.public !== 0) {
            const dividedWidth = containerWidth - MIN_WIDTH;
            privateWidth = dividedWidth * (props.private / totalValue);
            publicWidth = MIN_WIDTH;
            gdprWidth = dividedWidth * (props.gdpr / totalValue);
            if (privateWidth < MIN_WIDTH) {
              privateWidth = MIN_WIDTH;
              gdprWidth = containerWidth - 2 * MIN_WIDTH;
            } else if (gdprWidth < MIN_WIDTH) {
              gdprWidth = MIN_WIDTH;
              privateWidth = containerWidth - 2 * MIN_WIDTH;
            }
          } else if (privateWidth < MIN_WIDTH && props.private !== 0) {
            const dividedWidth = containerWidth - MIN_WIDTH;
            publicWidth = dividedWidth * (props.public / totalValue);
            gdprWidth = dividedWidth * (props.gdpr / totalValue);
            if (gdprWidth < MIN_WIDTH) {
              gdprWidth = MIN_WIDTH;
              publicWidth = containerWidth - 2 * MIN_WIDTH;
            } else if (publicWidth < MIN_WIDTH) {
              publicWidth = MIN_WIDTH;
              gdprWidth = containerWidth - 2 * MIN_WIDTH;
            }
            privateWidth = MIN_WIDTH;
          } else if (gdprWidth < MIN_WIDTH && props.gdpr !== 0) {
            const dividedWidth = containerWidth - MIN_WIDTH;
            privateWidth = dividedWidth * (props.private / totalValue);
            publicWidth = dividedWidth * (props.public / totalValue);
            if (privateWidth < MIN_WIDTH) {
              privateWidth = MIN_WIDTH;
              publicWidth = containerWidth - 2 * MIN_WIDTH;
            } else if (privateWidth <= MIN_WIDTH) {
              privateWidth = MIN_WIDTH;
              publicWidth = containerWidth - 2 * MIN_WIDTH;
            }
            gdprWidth = MIN_WIDTH;
          }
        }
      }

      setPublicValue(`${publicWidth}px`);
      setPrivateValue(`${privateWidth}px`);
      setGdprValue(`${gdprWidth}px`);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', calculateElemetsValues);
    return () => {
      window.removeEventListener('resize', calculateElemetsValues);
    };
  });

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      calculateElemetsValues();
    }, 50);
    return () => window.clearTimeout(timeout);
  });

  const formatTime = (value: number) => {
    const second = value % 60;
    const min = ((value - second) / 60) % 60;
    const hour = ((value - min * 60 - second) / 3600) % 24;
    const day = (value - min * 60 - second - hour * 3600) / (24 * 3600);
    if (day === 0) {
      if (hour === 0) {
        if (min === 0) {
          return `${second}s`;
        }
        return `${min}m ${second}s`;
      }
      return `${hour}h ${min}m ${second}s`;
    }
    return `${day}d ${hour}h ${min}m ${second}s`;
  };

  const getRoundValue = (value: number): string => {
    let result = Number(value.toFixed(3));
    if (
      props.name === 'avgConsumption' ||
      props.name === 'consumptionLitres' ||
      props.name === 'consumptionKilowatts' ||
      props.name === 'consumptionKilograms'
    ) {
      result = Number(value.toFixed(1));
    }
    return props.decimalComma ? String(result).replace('.', ',') : String(result);
  };

  // const convertValue = (value: number) => {
  //   if (
  //     props.name === "parking" ||
  //     props.name === "duration" ||
  //     props.name === "rideDuration" ||
  //     props.name === "parkingDuration"
  //   ) {
  //     return formatTime(value);
  //   }
  //   return value >= 1000
  //     ? `${(value / 1000).toFixed()}K`
  //     : getRoundValue(value);
  // };

  const convertValue = (value: number, isTotal?: boolean) => {
    let result =
      // value >= 1000 && !isTotal
      //   ? `${(value / 1000).toFixed()}K`
      //   : String(value);
      String(value);
    switch (props.name) {
      case 'distance':
        result = `${getRoundValue(value)} km`;
        break;
      case 'parking':
        result = formatTime(value);
        break;
      case 'avgConsumption':
        result = `${getRoundValue(value)} l`;
        break;
      case 'duration':
        result = formatTime(value);
        break;
      case 'rideDuration':
        result = formatTime(value);
        break;
      case 'parkingDuration':
        result = formatTime(value);
        break;
      case 'decline':
        result = `${getRoundValue(value)} m`;
        break;
      case 'increase':
        result = `${getRoundValue(value)} m`;
        break;
      case 'avgSpeed':
        result = `${getRoundValue(value)} km/h`;
        break;
      case 'maxSpeed':
        result = `${getRoundValue(value)} km/h`;
        break;
      case 'consumptionLitres':
        result = `${getRoundValue(value)} l`;
        break;
      case 'consumptionKilowatts':
        result = `${getRoundValue(value)} kWh`;
        break;
      case 'consumptionKilograms':
        result = `${getRoundValue(value)} kg`;
        break;
      default:
    }
    return result;
  };

  const convertTooltipValue = (value: number) => {
    return convertValue(value, true);
  };

  return (
    <>
      <div
        data-testid="CmdGraphicSummary"
        className="mb-xs row no-gutters"
        style={{ flexWrap: 'nowrap' }}
      >
        <div
          className="col-auto pr-xs pl-0 text-truncate"
          title={props.title !== undefined ? props.title : undefined}
        >
          {props.title}:{' '}
        </div>
        <strong
          className="col-auto"
          style={{ whiteSpace: 'nowrap' }}
          data-cy={`overall-summary-${props.name}-value`}
        >
          {props.gdpr !== undefined ? (
            convertValue(props.public + props.private + props.gdpr, true)
          ) : (
            <i className="cmd-icon cmd-icon-gdpr-document-lock fs-4" />
          )}
        </strong>
      </div>
      <div
        data-testid="CmdGraphicSummaryElement"
        className="row no-gutters flex-nowrap e-ride-summary"
        id={`${props.name}-graph-container`}
        style={{ flexWrap: 'nowrap' }}
      >
        {props.public ? (
          <div
            id={`${props.name}-public`}
            data-testid="CmdGraphicSummaryPublic"
            className="col total-summary-active"
            style={{ width: publicValue }}
            title={convertTooltipValue(props.public)}
            data-cy={`overall-summary-${props.name}-public-value`}
          >
            <div className="text-truncate">{convertValue(props.public)}</div>
          </div>
        ) : (
          <div />
        )}
        {props.private ? (
          <div
            id={`${props.name}-private`}
            data-testid="CmdGraphicSummaryPrivate"
            className="col-auto private"
            style={{ width: privateValue }}
            title={convertTooltipValue(props.private)}
            data-cy={`overall-summary-${props.name}-private-value`}
          >
            <div className="text-truncate">{convertValue(props.private)}</div>
          </div>
        ) : (
          <div />
        )}
        {props.gdpr !== 0 ? (
          <div
            id={`${props.name}-gdpr`}
            data-testid="CmdGraphicSummaryGdpr"
            className="col-auto private-anonym"
            style={{ width: gdprValue }}
            title={props.gdpr !== undefined ? convertTooltipValue(props.gdpr) : undefined}
            data-cy={`overall-summary-${props.name}-gdpr-value`}
          >
            {props.gdpr !== undefined ? (
              <div className="text-truncate">{convertValue(props.gdpr)}</div>
            ) : (
              <i className="cmd-icon cmd-icon-gdpr-document-lock fs-4" />
            )}
          </div>
        ) : (
          <div />
        )}
      </div>
    </>
  );
}

// export default CmdGraphicSummary;
